import React, { useMemo } from 'react';
import useGameStore from '../../store/gameStore';
import { formatMoney } from '../../utils/format';
import { CFE_CONFIG, getVehicleTonnage, getEssieuRate } from '../../data/taxes';
import { Building2, Truck } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Fiscalité — onglet Taxes locales (CFE + taxe à l'essieu). Liste les
 * véhicules > 3,5T et le détail semestriel par véhicule. Extracted from
 * Fiscalite.jsx on 28/05/2026.
 */
export default function LocalTaxesTab() {
  const { cfePaid, cfeDue, payCfe, essieuDue, payTaxeEssieu, essieuPaid, vehicles, treasury } = useGameStore();

  const essieuDetails = useMemo(() => {
    return (vehicles || []).map(v => {
      const tonnage = getVehicleTonnage(v.tier);
      const rate = getEssieuRate(tonnage);
      return { vehicle: v, tonnage, annualRate: rate, semiAnnual: Math.round(rate / 2) };
    }).filter(d => d.annualRate > 0);
  }, [vehicles]);

  // 06/02/2026 — P1 retour user prod : le bouton « Payer la taxe à l'essieu »
  // semblait inopérant : payTaxeEssieu poussait une `taxAlert` (Fonds
  // insuffisants) mais aucun toast direct n'apparaissait. On ajoute un
  // feedback explicite (toast.success / toast.error) + on désactive le
  // bouton si la trésorerie est insuffisante (tooltip pédagogique).
  const cfeInsufficient = cfeDue > 0 && (treasury || 0) < cfeDue;
  const essieuInsufficient = essieuDue > 0 && (treasury || 0) < essieuDue;
  const handlePayCfe = () => {
    const ok = payCfe();
    if (ok) toast.success(`CFE payée — ${formatMoney(cfeDue)}`);
    else toast.error('Fonds insuffisants pour la CFE. Augmente la trésorerie d\'abord.');
  };
  const handlePayEssieu = () => {
    const ok = payTaxeEssieu();
    if (ok) toast.success(`Taxe à l'essieu payée — ${formatMoney(essieuDue)}`);
    else toast.error(`Fonds insuffisants (${formatMoney(essieuDue)} dû). Trésorerie : ${formatMoney(treasury || 0)}.`);
  };

  return (
    <div className="space-y-4">
      {/* CFE */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
        <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
          <Building2 className="w-4 h-4" /> CFE — {CFE_CONFIG.label}
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          Taxe annuelle basée sur la valeur locative de vos locaux. Due en décembre.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-ink-700/30 rounded-lg p-3 text-center">
            <p className="text-[10px] text-slate-500">Statut</p>
            <p className={`text-sm font-bold ${cfePaid ? 'text-money' : cfeDue > 0 ? 'text-warn' : 'text-slate-500'}`}>
              {cfePaid ? 'Payée' : cfeDue > 0 ? `${formatMoney(cfeDue)} dû` : 'Non échue'}
            </p>
          </div>
          <div className="bg-ink-700/30 rounded-lg p-3 text-center">
            <p className="text-[10px] text-slate-500">Échéance</p>
            <p className="text-sm font-bold text-slate-300">Mois 12</p>
          </div>
        </div>
        {cfeDue > 0 && (
          <button
            onClick={handlePayCfe}
            disabled={cfeInsufficient}
            title={cfeInsufficient ? `Trésorerie insuffisante (${formatMoney(treasury || 0)} dispo, ${formatMoney(cfeDue)} dû)` : undefined}
            className={`mt-3 w-full py-2 rounded-lg text-xs font-bold transition-colors ${cfeInsufficient ? 'bg-slate-600 text-slate-300 cursor-not-allowed opacity-70' : 'bg-accent text-white hover:bg-accent-hover'}`}
            data-testid="pay-cfe-local-btn"
          >
            {cfeInsufficient ? `Fonds insuffisants (${formatMoney(cfeDue)})` : `Payer la CFE (${formatMoney(cfeDue)})`}
          </button>
        )}
      </div>

      {/* Taxe à l'essieu */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
        <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
          <Truck className="w-4 h-4" /> Taxe à l&apos;essieu
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          Taxe semestrielle sur les véhicules de plus de 3,5 tonnes. Janvier et juillet.
        </p>

        {essieuDetails.length === 0 ? (
          <p className="text-xs text-slate-500 text-center py-2">Aucun véhicule soumis (tous &le; 3,5T)</p>
        ) : (
          <div className="space-y-2 mb-3">
            {essieuDetails.map((d) => (
              <div key={`${d.vehicle.id}-essieu`} className="flex items-center justify-between text-xs bg-ink-700/30 rounded-lg p-2">
                <span className="text-slate-300">{d.vehicle.brand} {d.vehicle.model}</span>
                <span className="text-slate-500">{d.tonnage}T</span>
                <span className="font-medium text-foreground">{formatMoney(d.semiAnnual)} / semestre</span>
              </div>
            ))}
          </div>
        )}

        {essieuDue > 0 && (
          <button
            onClick={handlePayEssieu}
            disabled={essieuInsufficient}
            title={essieuInsufficient ? `Trésorerie insuffisante (${formatMoney(treasury || 0)} dispo, ${formatMoney(essieuDue)} dû)` : undefined}
            className={`w-full py-2 rounded-lg text-xs font-bold transition-colors ${essieuInsufficient ? 'bg-slate-600 text-slate-300 cursor-not-allowed opacity-70' : 'bg-accent text-white hover:bg-accent-hover'}`}
            data-testid="pay-essieu-local-btn"
          >
            {essieuInsufficient ? `Fonds insuffisants (${formatMoney(essieuDue)})` : `Payer la taxe à l'essieu (${formatMoney(essieuDue)})`}
          </button>
        )}

        {essieuPaid.length > 0 && (
          <div className="mt-3 border-t border-ink-600 pt-2">
            <p className="text-[10px] text-slate-500">Paiements effectués :</p>
            {essieuPaid.map((p) => (
              <p key={`${p.semester}-${p.year}`} className="text-[10px] text-money">S{p.semester} A{p.year} — {formatMoney(p.amount)}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
